import type { Detail, Annotation } from "building-types/annotation/annotationTypes";
import type { Asset } from "building-types/entities/asset";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { SheetAssetGeometry, SheetAssetGeometryProps } from "building-types/views/sheetGeometryTypes";
import { sheetAssetGeometryNew } from "building-types/views/sheetGeometryTypes";
import type { Table } from "common/data/table";
import { assertUnreachable } from "common/utils/assertUnreachable";
import degreeToRadian from "common/utils/degreeToRadian";
import { PlacementYAnchor, PlacementXAnchor } from "common/utils/textAlignment";
import { generateQRCodeImage } from "./QRCode";

export function makeDetailAnnotationSheetGeometry(
  detail: Detail,
  assetCatalog: Table<Asset>,
  handle?: DocEntityHandle,
  buildingEntity?: Annotation,
): SheetAssetGeometry | null {
  switch (detail.detailType) {
    case "ASSET_REFERENCE": {
      const { x, y, scale, rotation, assetId } = detail;
      if (!assetId) {
        return null;
      }
      const asset = assetCatalog[assetId];
      if (!asset) {
        return null;
      }
      return sheetAssetGeometryNew({
        handle: detail.handle ?? handle,
        x,
        y,
        scale,
        rotation: degreeToRadian(rotation),
        buildingEntity,
        assetType: asset.srcType,
        asset,
        placementYAnchor: PlacementYAnchor.BOTTOM,
        placementXAnchor: PlacementXAnchor.LEFT,
        // we need to make typescript happy here with this cast.
        // we know that asset.srcType === assetType
      } as SheetAssetGeometryProps);
    }
    case "INLINE_IMAGE": {
      const { x, y, width, height, rotation, url } = detail;
      return makeImageAssetSheetGeometry(
        url ?? "",
        x,
        y,
        width,
        height,
        rotation,
        detail.handle ?? handle,
        buildingEntity,
      );
    }
    case "QR_CODE": {
      const { x, y, size, url } = detail;
      const makeQRCodeGeom = (url: string = "") =>
        makeImageAssetSheetGeometry(url, x, y, size, size, 0, detail.handle ?? handle);

      if (!url) {
        return makeQRCodeGeom();
      }

      // Annotation parameters are expected to be evaluated here, so we can cast to string.
      // TODO: This would be neater if we had an evaluated annotation type.
      const qrCodeImage = generateQRCodeImage(url as string);
      if (!qrCodeImage) {
        return makeQRCodeGeom();
      }

      return makeQRCodeGeom(qrCodeImage);
    }
    default: {
      assertUnreachable(detail);
    }
  }
}

function makeImageAssetSheetGeometry(
  url: string,
  x: number,
  y: number,
  width: number,
  height: number,
  rotationDegrees: number | undefined,
  handle?: DocEntityHandle,
  buildingEntity?: Annotation,
): SheetAssetGeometry {
  return sheetAssetGeometryNew({
    handle,
    x,
    y,
    width,
    height,
    rotation: rotationDegrees ? degreeToRadian(rotationDegrees) : undefined,
    assetType: "IMAGE_LEGACY",
    asset: {
      srcType: "IMAGE_LEGACY",
      srcPath: url,
    },
    placementYAnchor: PlacementYAnchor.BOTTOM,
    placementXAnchor: PlacementXAnchor.LEFT,
    buildingEntity,
  });
}
